import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { RelationshipHttpService } from 'src/providers/relationship-http.service';
import { Relationship } from 'src/app/core/models/relationship/relationship.model';

/**
 * Carrier Agency Profile Resolver (Lightweight)
 *
 * Pre-loads ONLY the minimal data needed for the page shell (header, side-rail, footer).
 * This allows the page structure to render quickly (~200ms) while child content areas
 * show loading indicators for their specific data.
 *
 * Strategy:
 * - Parent resolver (this): Loads relationship only (needed for tab visibility)
 * - Child components: Load their own data, show <app-wait> during load
 * - Result: Page shell renders fast, content loads progressively
 */
export interface CarrierAgencyProfileShellData {
    relationship: Relationship | null;
    carrierNetworkId: string | null;
    error?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CarrierAgencyProfileResolverService implements Resolve<CarrierAgencyProfileShellData> {

    constructor(
        private relationshipHttpService: RelationshipHttpService
    ) {}

    resolve(route: ActivatedRouteSnapshot): Observable<CarrierAgencyProfileShellData> {
        const carrierNetworkId = route.queryParamMap.get('carrierNetworkId');
        const relationshipId = route.queryParamMap.get('relationshipId');

        // Validate required parameters
        if (!carrierNetworkId || !relationshipId) {
            // The carrierRequest child is self-sufficient - it loads by producerRequestId and does
            // NOT need carrierNetworkId/relationshipId. Under UI-Router the child inherited those
            // from the parent state; Angular Router does not inherit query params across navigate(),
            // and the DocuSign "Return to Sircon" history.back() lands here carrying only
            // producerRequestId. Treat a param-less shell for this child as a valid (non-error) empty
            // shell so the child renders in the outlet without the parent painting spurious error
            // boxes. Other children (overview/profile/etc.) genuinely need these params, so they
            // still surface the error. See DEV-43194.
            if (route.firstChild?.routeConfig?.path === 'carrierRequest') {
                return of({
                    relationship: null,
                    carrierNetworkId: null,
                    error: false
                });
            }
            console.warn('[CarrierAgencyProfileResolver] Missing required parameters');
            return of({
                relationship: null,
                carrierNetworkId: null,
                error: true
            });
        }

        // Load ONLY relationship - this is fast and needed for side-rail tabs
        return this.relationshipHttpService.getRelationshipById(relationshipId).pipe(
            map((relationship: Relationship) => {
                return {
                    relationship,
                    carrierNetworkId,
                    error: false
                };
            }),
            catchError((error) => {
                console.error('[CarrierAgencyProfileResolver] Failed to load relationship:', error);
                return of({
                    relationship: null,
                    carrierNetworkId: null,
                    error: true
                });
            })
        );
    }
}
